import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { createApiClient } from '../lib/api-client.js';
import type { CurrentTenant, CurrentUser } from '../lib/types.js';

interface AuthState {
  status: 'loading' | 'anonymous' | 'authenticated';
  user: CurrentUser | null;
  /** 工厂端身份才有；内部飞书账号是 null */
  tenant: CurrentTenant | null;
  refresh: () => Promise<void>;
}

const AuthCtx = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<{
    status: 'loading' | 'anonymous' | 'authenticated';
    user: CurrentUser | null;
    tenant: CurrentTenant | null;
  }>({ status: 'loading', user: null, tenant: null });

  async function fetchMe() {
    try {
      const api = createApiClient();
      const res = await api.me();
      if (res.user) {
        setState({
          status: 'authenticated',
          user: res.user,
          tenant: (res as { tenant?: CurrentTenant | null }).tenant ?? null,
        });
      } else {
        setState({ status: 'anonymous', user: null, tenant: null });
      }
    } catch {
      setState({ status: 'anonymous', user: null, tenant: null });
    }
  }

  useEffect(() => {
    void fetchMe();
  }, []);

  return <AuthCtx.Provider value={{ ...state, refresh: fetchMe }}>{children}</AuthCtx.Provider>;
}

/**
 * 子树里换成另一家工厂的身份：user 不变，只换 tenant。
 * 自营站（Strand）就是一家工厂 —— sitebox 里的消息页用它把工厂端那一屏原样挂进来。
 */
export function TenantOverride({
  tenant,
  children,
}: {
  tenant: CurrentTenant;
  children: ReactNode;
}): JSX.Element {
  const v = useAuth();
  return <AuthCtx.Provider value={{ ...v, tenant }}>{children}</AuthCtx.Provider>;
}

export function useAuth(): AuthState {
  const v = useContext(AuthCtx);
  if (!v) throw new Error('useAuth must be inside <AuthProvider>');
  return v;
}
